import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import RelationshipEditor from '../Common/RelationshipEditor';
import RelationshipDisplay from '../Common/RelationshipDisplay';
import RelationshipGraph from '../Common/RelationshipGraph';
import AttachmentManager from '../Common/AttachmentManager';
import ImageAttachmentDisplay from '../Common/ImageAttachmentDisplay';
import { wikiTheme } from '../wikiTheme';
import * as wikiService from '../../../services/wikiService';
import { useNavigate } from 'react-router-dom';

/**
 * Detail view for a faction entry with relationships
 */
const FactionDetailPage = ({ entryId, onBack, onEdit, onDelete }) => {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const navigate = useNavigate();

  const loadEntry = useCallback(async () => {
    try {
      setLoading(true);
      const data = await wikiService.getWikiEntry(entryId);
      setEntry(data);

      // Load member details
      if (data.relationships) {
        const memberRelationships = data.relationships.filter(rel => rel.type === 'has_member');
        const memberPromises = memberRelationships.map(rel => 
          wikiService.getWikiEntry(rel.entryId).catch(err => {
            console.error('Error loading member:', err);
            return null;
          })
        );
        const memberData = await Promise.all(memberPromises);
        setMembers(memberData.filter(Boolean));
      } else {
        setMembers([]);
      }
    } catch (error) {
      console.error('Error loading entry:', error);
    } finally {
      setLoading(false);
    }
  }, [entryId]);

  useEffect(() => {
    loadEntry();
  }, [loadEntry]);

  // Scroll to top when component mounts or entryId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [entryId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!entry) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Faction not found</Typography>
        <Button onClick={onBack} sx={{ mt: 2 }}>
          <ArrowBackIcon sx={{ mr: 1 }} />
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: wikiTheme.dark, p: 2, borderRadius: 1 }}>
      <Button 
        onClick={onBack} 
        sx={{ mb: 2, color: wikiTheme.primary, fontFamily: wikiTheme.font }}
      >
        <ArrowBackIcon sx={{ mr: 1 }} />
        Back to Factions
      </Button>

      <Paper sx={{ p: 3, backgroundColor: wikiTheme.mediumGray }}>
        <Typography variant="h4" sx={{ mb: 2, color: wikiTheme.secondary, fontFamily: wikiTheme.font }}>
          🏰 {entry.title}
        </Typography>

        {entry.tags?.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {entry.tags.map((tag) => (
              <Chip 
                key={tag} 
                label={tag}
                sx={{
                  backgroundColor: wikiTheme.primary,
                  color: wikiTheme.dark,
                  fontFamily: wikiTheme.font,
                  pointerEvents: 'none'
                }}
              />
            ))}
          </Box>
        )}

        {/* Image Attachments and Description - side by side for single image, stacked for multiple */}
        {entry.attachments && entry.attachments.length > 0 && entry.attachments.some(att => att.type && att.type.startsWith('image/')) ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: entry.attachments.filter(att => att.type && att.type.startsWith('image/')).length === 1 ? 'row' : 'column' },
            gap: 3,
            mb: 3 
          }}>
            <Box
              sx={{
                flex: 1,
                p: 2,
                bgcolor: wikiTheme.dark,
                borderRadius: 1,
                color: wikiTheme.secondary,
                fontFamily: wikiTheme.font,
                borderLeft: `3px solid ${wikiTheme.primary}`,
              }}
              dangerouslySetInnerHTML={{ __html: entry.description }}
            />
            <Box sx={{ 
              flex: entry.attachments.filter(att => att.type && att.type.startsWith('image/')).length === 1 ? '0 0 auto' : '1',
              minWidth: { xs: '100%', md: entry.attachments.filter(att => att.type && att.type.startsWith('image/')).length === 1 ? '300px' : 'auto' },
              maxWidth: { xs: '100%', md: entry.attachments.filter(att => att.type && att.type.startsWith('image/')).length === 1 ? '400px' : '100%' }
            }}>
              <ImageAttachmentDisplay attachments={entry.attachments} />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: wikiTheme.dark,
              borderRadius: 1,
              color: wikiTheme.secondary,
              fontFamily: wikiTheme.font,
              borderLeft: `3px solid ${wikiTheme.primary}`,
            }}
            dangerouslySetInnerHTML={{ __html: entry.description }}
          />
        )}

        {/* Members List */}
        {members.length > 0 && (
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                color: wikiTheme.primary, 
                fontFamily: wikiTheme.font, 
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <PersonIcon /> Members ({members.length})
            </Typography>
            <List sx={{ 
              bgcolor: wikiTheme.dark, 
              borderRadius: 1,
              p: 0
            }}>
              {members.map((member, index) => (
                <ListItem 
                  key={member.id}
                  disablePadding
                  sx={{
                    borderBottom: index < members.length - 1 ? `1px solid ${wikiTheme.lightGray}` : 'none'
                  }}
                >
                  <ListItemButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/wiki/${member.id}`);
                    }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      display: 'flex',
                      gap: 2,
                      '&:hover': {
                        backgroundColor: wikiTheme.mediumGray,
                        borderLeft: `4px solid ${wikiTheme.primary}`,
                        pl: 1.5
                      }
                    }}
                  >
                    {/* Small Image Preview */}
                    {member.attachments?.some(att => att.type?.startsWith('image/')) && (
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: `2px solid #66bb6a`,
                          flexShrink: 0,
                          backgroundColor: wikiTheme.dark,
                        }}
                      >
                        <img
                          src={member.attachments.find(att => att.type?.startsWith('image/')).url}
                          alt={member.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      </Box>
                    )}
                    
                    <ListItemText
                      primary={
                        <Typography sx={{ 
                          color: wikiTheme.secondary, 
                          fontFamily: wikiTheme.font,
                          fontWeight: 500
                        }}>
                          👥 {member.title}
                        </Typography>
                      }
                      secondary={
                        member.description && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: wikiTheme.tertiary, 
                              fontFamily: wikiTheme.font,
                              mt: 0.5
                            }}
                            dangerouslySetInnerHTML={{ 
                              __html: member.description.substring(0, 100) + (member.description.length > 100 ? '...' : '')
                            }}
                          />
                        )
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Filter out has_member relationships - they're shown in Members section */}
        {(() => {
          const nonMemberRelationships = entry.relationships?.filter(rel => rel.type !== 'has_member') || [];
          return nonMemberRelationships.length > 0 && (
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3,
              mb: 3
            }}>
              {/* Left column - Relationships */}
              <Box>
                <Accordion 
                  defaultExpanded 
                  sx={{ 
                    bgcolor: wikiTheme.dark,
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: wikiTheme.primary }} />}>
                    <Typography sx={{ color: wikiTheme.primary, fontFamily: wikiTheme.font, fontWeight: 'bold' }}>
                      Relationships ({nonMemberRelationships.length})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <RelationshipDisplay 
                      relationships={nonMemberRelationships} 
                      entryId={entryId}
                      onRelationshipChange={loadEntry}
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>

              {/* Right column - Relationship Graph */}
              <Box>
                <Accordion 
                  defaultExpanded
                  sx={{ 
                    bgcolor: wikiTheme.dark,
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: wikiTheme.primary }} />}>
                    <Typography sx={{ color: wikiTheme.primary, fontFamily: wikiTheme.font, fontWeight: 'bold' }}>
                      Relationship Graph
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <RelationshipGraph
                      entryId={entryId}
                      entryTitle={entry.title}
                      entryType="faction"
                      relationships={entry.relationships || []}
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Box>
          );
        })()}

        {/* Manage Relationships */}
        <Box sx={{ mt: 3, mb: 3 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 2, 
              color: wikiTheme.primary, 
              fontFamily: wikiTheme.font,
              fontWeight: 'bold' 
            }}
          >
            Manage Relationships
          </Typography>
          <RelationshipEditor
            entryId={entryId}
            currentRelationships={entry.relationships || []}
            onRelationshipChange={loadEntry}
            entryType="faction"
          />
        </Box>

        {/* Manage Attachments */}
        <Box sx={{ mt: 3 }}>
          <Typography sx={{ mb: 2, color: wikiTheme.tertiary, fontFamily: wikiTheme.font, fontWeight: 'bold' }}>
            Attachments
          </Typography>
          <AttachmentManager
            entryId={entryId}
            attachments={entry.attachments || []}
            onAttachmentsChange={(newAttachments) => {
              setEntry({ ...entry, attachments: newAttachments });
              // Reload the entry to ensure we have the latest data from DB
              loadEntry();
            }}
          />
        </Box>

        {/* Edit/Delete Buttons */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={onEdit}
            sx={{
              backgroundColor: wikiTheme.primary,
              color: wikiTheme.dark,
              fontFamily: wikiTheme.font,
              '&:hover': {
                backgroundColor: wikiTheme.secondary,
              },
            }}
          >
            Edit Faction
          </Button>
          <Button
            variant="outlined"
            onClick={onDelete}
            sx={{
              borderColor: '#e74c3c',
              color: '#e74c3c',
              fontFamily: wikiTheme.font,
              '&:hover': {
                borderColor: '#c0392b',
                backgroundColor: 'rgba(231, 76, 60, 0.1)',
              },
            }}
          >
            Delete Faction
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default FactionDetailPage;
